import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Switch,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Box,
    Divider,
    IconButton
} from '@mui/material';
import {
    Shield,
    Assessment,
    Campaign,
    Close
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const CookieSettingsModal = ({ open, onClose, preferences, onSave }) => {
    const { t } = useTranslation();
    const [localPrefs, setLocalPrefs] = useState(preferences);

    const handleToggle = (category) => {
        if (category === 'essential') return; // Cannot toggle essential
        setLocalPrefs(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleSave = () => {
        onSave(localPrefs);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('cookieConsent.settingsTitle', 'Cookie Settings')}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('cookieConsent.settingsDesc', 'We use cookies to enhance your experience. You can choose which categories to enable below.')}
                </Typography>

                <List disablePadding>
                    {/* Essential */}
                    <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemIcon>
                            <Shield color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('cookieConsent.essentialTitle', 'Essential Cookies')}
                            secondary={t('cookieConsent.essentialDesc', 'Required for the website to function properly. Cannot be disabled.')}
                        />
                        <Switch checked={true} disabled color="primary" />
                    </ListItem>
                    <Divider variant="inset" component="li" />

                    {/* Analytics */}
                    <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemIcon>
                            <Assessment color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('cookieConsent.analyticsTitle', 'Analytics Cookies')}
                            secondary={t('cookieConsent.analyticsDesc', 'Help us understand how visitors interact with our website to improve performance.')}
                        />
                        <Switch
                            checked={localPrefs.analytics}
                            onChange={() => handleToggle('analytics')}
                            color="primary"
                        />
                    </ListItem>
                    <Divider variant="inset" component="li" />

                    {/* Marketing */}
                    <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemIcon>
                            <Campaign color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={t('cookieConsent.marketingTitle', 'Marketing Cookies')}
                            secondary={t('cookieConsent.marketingDesc', 'Used to track visitors across websites to deliver relevant advertisements.')}
                        />
                        <Switch
                            checked={localPrefs.marketing}
                            onChange={() => handleToggle('marketing')}
                            color="primary"
                        />
                    </ListItem>
                </List>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleSave} variant="contained" sx={{ px: 4 }}>
                    {t('common.save', 'Save Preferences')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CookieSettingsModal;
